import { useEffect, useRef, useState } from 'react';
import { dayNumber, dailyHint, displayTitle } from '../lib/game';
import { useGame } from '../lib/useGame';
import { fireConfetti } from '../lib/confetti';
import { useApp } from '../app-context';
import GuessSearch from './GuessSearch';
import ResultsGrid from './ResultsGrid';
import PixelView from './PixelView';
import YesterdayCard from './YesterdayCard';
import VictoryCard from './VictoryCard';
import FootChips from './FootChips';
import ShareModal from './ShareModal';

export default function Game({ mode }: { mode: string }) {
  const { lang, t } = useApp();
  const game = useGame(mode);
  const [showShare, setShowShare] = useState(false);
  const [showHint, setShowHint] = useState(false);
  const done = game.status !== 'playing';
  const hint = dailyHint(game.answer);

  // Fire confetti once when this session transitions to a win (best-practice §11.7).
  const prev = useRef(game.status);
  useEffect(() => {
    if (prev.current === 'playing' && game.status === 'won') fireConfetti();
    prev.current = game.status;
  }, [game.status]);

  return (
    <>
      <section className="panel">
        <div className="row1">
          <span className="tt">#{dayNumber()} — {t(`mode.${mode}`)}</span>
          <span className="tries">
            {done
              ? t('tries.done')
              : game.max == null
                ? t('tries.count', { n: game.guesses.length })
                : <span dangerouslySetInnerHTML={{ __html: t('tries.remaining', { n: `<b>${game.remaining}</b>`, max: game.max }) }} />}
          </span>
        </div>

        <GuessSearch excludeIds={game.guessedIds} disabled={done} onGuess={game.onGuess} />

        {!done && (
          showHint ? (
            <div className="hint"><span className="hint-tag">{t('hint.label')}</span> {t('hint.text', { decade: hint.decade, genre: hint.genre })}</div>
          ) : (
            <button className="hint-btn" onClick={() => setShowHint(true)}>{t('hint.show')}</button>
          )
        )}

        {mode === 'pixel' ? (
          <div className="pixelwrap">
            <PixelView answer={game.answer} guessCount={game.guesses.length} revealed={done} />
            {game.guesses.length > 0 ? (
              <ul className="guesslist">
                {[...game.results].reverse().map((r) => (
                  <li key={r.anime.id} className={r.isAnswer ? 'hit' : 'miss'}>
                    {r.anime.cover && <img src={r.anime.cover} alt="" width={30} height={42} loading="lazy" />}
                    <span className="gl-title">{displayTitle(r.anime)}</span>
                    <span className="gl-meta">{[r.anime.year, r.anime.studio].filter(Boolean).join(' · ')}</span>
                    <span className={`gl-mark ${r.isAnswer ? 'ok' : 'no'}`}>{r.isAnswer ? '✓' : '✕'}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="empty"><YesterdayCard image={game.yesterday.cover} title={displayTitle(game.yesterday)} line1={[game.yesterday.year, game.yesterday.studio].filter(Boolean).join(" · ")} line2={game.yesterday.genres.slice(0,3).join(" · ")} /></div>
            )}
          </div>
        ) : game.guesses.length === 0 ? (
          <div className="empty">
            <div>{t('empty.first')}</div>
            <YesterdayCard image={game.yesterday.cover} title={displayTitle(game.yesterday)} line1={[game.yesterday.year, game.yesterday.studio].filter(Boolean).join(" · ")} line2={game.yesterday.genres.slice(0,3).join(" · ")} />
          </div>
        ) : (
          <ResultsGrid results={game.results} />
        )}
      </section>

      {done && (
        <VictoryCard
          won={game.status === 'won'}
          image={game.answer.cover}
          title={displayTitle(game.answer)}
          meta={[game.answer.year, game.answer.studio, game.answer.genres.slice(0, 3).join(' · ')].filter(Boolean).join(' · ')}
          sub={game.status === 'won'
            ? t('victory.solvedIn', { n: game.results.length })
            : t('victory.tryTomorrow')}
        />
      )}

      <FootChips streak={game.streak} done={done} onShare={() => setShowShare(true)} />

      {showShare && (
        <ShareModal
          mode={mode}
          lang={lang}
          grid={
            mode === 'pixel'
              ? { type: 'squares', squares: game.results.map((r) => r.isAnswer) }
              : {
                  type: 'grid',
                  rows: game.results.map((r) => [r.year.state, r.season.state, r.episodes.state, r.studio.state, r.genres.state, r.score.state]),
                }
          }
          attempts={game.results.length}
          max={game.max}
          won={game.status === 'won'}
          streak={game.streak}
          day={dayNumber()}
          onClose={() => setShowShare(false)}
        />
      )}
    </>
  );
}
